import React, { MouseEvent, useEffect, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { SiteFooterQueryQuery } from "@graphqlTypes"

import { convertStringToKebabCase, isClientSide } from "@utils"

import AnchorLink from "@components/AnchorLink/AnchorLink"
import Badge, { BADGE_COLORS, BADGE_SIZES } from "@components/Badge/Badge"
import Icon, { ICON_TYPES } from "@components/Icon/Icon"

import SiteFooterSocialLinks from "./SiteFooterSocialLinks/SiteFooterSocialLinks"
import SiteFooterResponsibility from "./SiteFooterResponsibility/SiteFooterResponsibility"
import SiteFooterBigLogo from "./SiteFooterBigLogo/SiteFooterBigLogo"

import "./SiteFooter.scss"

declare global {
  interface Window {
    transcend: any
  }
}

const SiteFooter = () => {
  const [logoHighlightOpacity, setLogoHighlightOpacity] = useState<number>(0)
  const [clientXY, setClientXY] = useState<[x: number, y: number]>([0, 0])

  const { siteFooterData } = useStaticQuery<SiteFooterQueryQuery>(graphql`
    query SiteFooterQuery {
      siteFooterData: contentfulJsonData(contentful_id: {eq: "4ywB5F9sHaNQzi0wnwHtix"}) {
        json {
          columns {
            title
            items {
              title
              url
              html
              isNew
            }
          }
        }
      }
    }
  `)

  if (!siteFooterData?.json?.columns) return null

  const { columns } = siteFooterData.json

  // Add Do Not Sell consent manager pop-up using transcend API - https://github.com/transcend-io/consent-manager-ui#donotselldisclosure
  useEffect(() => {
    if (!isClientSide()) return

    const doNotSell = document.querySelector("#footerDoNotSell")

    const checkAPI = (timeout: number) => {
      const startTime = Date.now()

      const pollAPI = () => {
        if (window.transcend) {
          // API is loaded
          if (doNotSell) {
            doNotSell.addEventListener("click", (event) => {
              event.preventDefault()
              window.transcend.doNotSell(event)
            })
          }
        } else {
          // API has not yet loaded
          const currentTime = Date.now()
          const elapsedTime = currentTime - startTime

          if (elapsedTime >= timeout) {
            console.error('Timeout occurred while waiting for Transcend.')
            return
          }

          // Schedule another check
          setTimeout(pollAPI, 100)
        }
      }
      pollAPI()
    }

    // Specify polling timeout duration (in milliseconds)
    const timeoutDuration = 5000
    if (doNotSell) checkAPI(timeoutDuration)
  }, [])

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    setClientXY([e.clientX, e.clientY])
  }

  const handleMouseEnter = () => {
    setLogoHighlightOpacity(0.2)
  }

  const handleMouseLeave = () => {
    setLogoHighlightOpacity(0)
  }


  return (
    <footer
      className="SiteFooter"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="container">
        <div className="flex">
          {columns.map(column => !column ? null : (
            <div
              className={`SiteFooter--column ${convertStringToKebabCase(column.title)}`}
              key={column.title}
            >
              <h4 className="type--headline-medium-S">{column.title}</h4>
              {column.items && column.items.map(item => {
                if (!item) return null

                if (!item.url && item.html) {
                  return (
                    <span
                      key={item.html}
                      dangerouslySetInnerHTML={{ __html: item.html }}
                    />
                  )
                }

                if (!item.url) return null

                return (
                  <AnchorLink
                    key={item.title}
                    href={item.url}
                  >
                    {item.title}
                    {item.isNew && (
                      <Badge
                        color={BADGE_COLORS.COBALT}
                        size={BADGE_SIZES.XS}
                      >
                        NEW
                      </Badge>
                    )}
                    {item.title === "Status" && (
                      <Badge
                        className="status-badge"
                        color={BADGE_COLORS.CHARCOAL}
                        size={BADGE_SIZES.XS}
                      >
                        <svg
                          style={{
                            verticalAlign: "baseline",
                            width: "calc(var(--font-size--XS) * .8)",
                            height: "calc(var(--font-size--XS) * .8)"
                          }}
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.96873 16.3535L10.2052 5.85653C10.9418 4.38476 13.0388 4.38515 13.7748 5.85718L19.0391 16.3542C19.704 17.6841 18.7388 19.25 17.2541 19.25H6.75335C5.26832 19.25 4.30314 17.6835 4.96873 16.3535Z"
                            stroke="var(--color--charcoal-400)"
                            fill="var(--color--charcoal-400)"
                            strokeWidth="1.5"
                            strokeLinecap="square"
                            strokeLinejoin="round"
                          />
                        </svg>
                        99.99%
                      </Badge>
                    )}
                    {item.url.includes("//") && (
                      <Icon
                        size="calc(var(--font-size--XS) * .8)"
                        type={ICON_TYPES["arrow-up-right"]}
                      />
                    )}
                  </AnchorLink>
                )
              })}
            </div>
          ))}
          <div className="SiteFooter--column desktop-meta">
            <SiteFooterSocialLinks />
            <SiteFooterResponsibility />
            <div className="type--text-XS color--charcoal-600 text-align--right">&copy; {(new Date()).getFullYear()} Help&nbsp;Scout</div>
          </div>
          <div className="SiteFooter--column mobile-meta">
            <SiteFooterResponsibility />
          </div>
          <div className="SiteFooter--column mobile-meta social-links-copyright">
            <SiteFooterSocialLinks />
            <div className="type--text-XS color--charcoal-600">&copy; {(new Date()).getFullYear()} Help&nbsp;Scout</div>
          </div>
          {/* <div className="tagline flex-item--1">
            <span className="type--serif-2XL font-size--XL color--charcoal-300"><em>The all-in-one platform for</em></span>
            {" "}
            <span className="type--headline-medium-M color--charcoal-300">delightful conversations</span>
          </div> */}
        </div>
      </div>
      <SiteFooterBigLogo
        clientX={clientXY[0]}
        clientY={clientXY[1]}
        opacity={logoHighlightOpacity}
      />
    </footer>
  )
}

export default SiteFooter
